import { ChevronDown, ChevronRight } from "lucide-react";
import { type ReactNode, useState } from "react";

import type { DistrictRow } from "@busright/data-contract";
import { RAG } from "@busright/ui";

import { formatInt, formatPct } from "@/lib/format";

import { EmptyState } from "./EmptyState";
import { RiskTag } from "./RiskTag";

// =================================================================================================
// DistWorklistTable — the ranked district worklist with inline drill-down.
//
// Clicking a district row EXPANDS it in place to reveal that district's at-risk composition
// (CRIT / WARN / OK device counts + critical share). The /api/districts payload exposes per-district
// risk COUNTS but not per-device serials, so the expansion is honest about that: it surfaces the
// at-risk load and links onward to Fleet Health (the per-device tail surface). Once the endpoint
// ships device rows, each row here becomes a /device/:serial link.
//
// The header chevron + expanded panel are keyboard-accessible: each row is a real toggle button
// (aria-expanded), and the expanded device-level CTA is a focusable navigation control.
// =================================================================================================

const criticalPct = (r: DistrictRow): number => {
  if (!r.riskCounts || !r.deviceCount || r.deviceCount === 0) {
    return 0;
  }
  return (r.riskCounts.critical / r.deviceCount) * 100;
};

export interface DistWorklistTableProps {
  readonly rows: readonly DistrictRow[];
  // When true, render the rank (#) + match-method columns (full worklist). When false, render the
  // compact at-risk view (no rank / match columns).
  readonly showRank?: boolean;
  // Called when the user drills from an expanded district into the per-device tail.
  readonly onOpenDeviceTail: () => void;
  readonly emptyTitle?: string;
}

export const DistWorklistTable = ({
  rows,
  showRank = false,
  onOpenDeviceTail,
  emptyTitle = "No districts",
}: DistWorklistTableProps): ReactNode => {
  const [expanded, setExpanded] = useState<string | null>(null);
  if (rows.length === 0) {
    return <EmptyState title={emptyTitle} />;
  }
  const colSpan = showRank ? 9 : 7;
  const toggle = (key: string): void => {
    setExpanded((cur) => (cur === key ? null : key));
  };
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="data-table">
        <thead>
          <tr>
            <th aria-label="Expand" />
            {showRank ? <th className="num">#</th> : null}
            <th>District</th>
            <th>State</th>
            {showRank ? <th>Match</th> : null}
            <th className="num">Devices</th>
            <th className="num">OK</th>
            <th className="num">WARN</th>
            <th className="num">CRIT</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const key = `${r.districtId}-${r.accountName}`;
            const isOpen = expanded === key;
            const crit = r.riskCounts?.critical ?? 0;
            const warn = r.riskCounts?.warn ?? 0;
            const ok = r.riskCounts?.ok ?? 0;
            return (
              <DistWorklistRow
                key={key}
                rowKey={key}
                row={r}
                rank={i + 1}
                showRank={showRank}
                isOpen={isOpen}
                colSpan={colSpan}
                crit={crit}
                warn={warn}
                ok={ok}
                onToggle={toggle}
                onOpenDeviceTail={onOpenDeviceTail}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

interface DistWorklistRowProps {
  readonly rowKey: string;
  readonly row: DistrictRow;
  readonly rank: number;
  readonly showRank: boolean;
  readonly isOpen: boolean;
  readonly colSpan: number;
  readonly crit: number;
  readonly warn: number;
  readonly ok: number;
  readonly onToggle: (key: string) => void;
  readonly onOpenDeviceTail: () => void;
}

const DistWorklistRow = ({
  rowKey,
  row,
  rank,
  showRank,
  isOpen,
  colSpan,
  crit,
  warn,
  ok,
  onToggle,
  onOpenDeviceTail,
}: DistWorklistRowProps): ReactNode => (
  <>
    <tr
      className="dist-row"
      onClick={() => onToggle(rowKey)}
      style={{ cursor: "pointer" }}
      aria-expanded={isOpen}
    >
      <td>
        <span className="dist-chevron" aria-hidden>
          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
      </td>
      {showRank ? <td className="num mono">{rank}</td> : null}
      <td>{row.accountName}</td>
      <td className="mono">{row.state ?? "—"}</td>
      {showRank ? <td className="mono">{row.matchMethod ?? "—"}</td> : null}
      <td className="num mono">{formatInt(row.deviceCount ?? 0)}</td>
      <td className="num mono" style={{ color: RAG.OK }}>
        {formatInt(ok)}
      </td>
      <td className="num mono" style={{ color: RAG.WARN }}>
        {formatInt(warn)}
      </td>
      <td className="num">
        {crit > 0 ? (
          <RiskTag tier="CRITICAL" />
        ) : (
          <span className="mono" style={{ color: RAG.CRITICAL }}>
            0
          </span>
        )}
      </td>
    </tr>
    {isOpen ? (
      <tr className="dist-expand-row">
        <td colSpan={colSpan}>
          <div className="dist-expand">
            <div className="dist-expand-head">
              <span className="dist-expand-title">{row.accountName} — at-risk devices</span>
              <span className="dist-expand-sub">
                {formatInt(crit)} CRITICAL · {formatInt(warn)} WARN of{" "}
                {formatInt(row.deviceCount ?? 0)} devices ({formatPct(criticalPct(row))} critical)
              </span>
            </div>
            <div className="dist-tier-strip">
              <span className="dist-tier crit">
                <span className="dist-tier-val br-tabular-nums">{formatInt(crit)}</span>
                <span className="dist-tier-label">CRITICAL</span>
              </span>
              <span className="dist-tier warn">
                <span className="dist-tier-val br-tabular-nums">{formatInt(warn)}</span>
                <span className="dist-tier-label">WARN</span>
              </span>
              <span className="dist-tier ok">
                <span className="dist-tier-val br-tabular-nums">{formatInt(ok)}</span>
                <span className="dist-tier-label">OK</span>
              </span>
            </div>
            <p className="dist-expand-note">
              Per-device serials aren&rsquo;t on this endpoint yet — open Fleet Health for the
              device-level at-risk tail. Each device will link to its Device&nbsp;360 view once
              serials ship.
            </p>
            <button
              type="button"
              className="dist-expand-cta"
              onClick={(e) => {
                e.stopPropagation();
                onOpenDeviceTail();
              }}
            >
              Open Fleet Health — device-level tail
            </button>
          </div>
        </td>
      </tr>
    ) : null}
  </>
);
